import { Suspense } from "react"
import { lazyWithReload } from "./lib/chunk-reload"
import { RouteError } from "./components/route-error"
import { createBrowserRouter } from "react-router-dom"
import { AppShell } from "./components/app-shell"
import { HomeRedirect, RequirePermission, RequireSession } from "./components/guards"
import { ActivityPage } from "./routes/activity"
import { SecurityLogPage } from "./routes/security-log"
import { MessagesPage } from "./routes/messages"
import { IssuesPage } from "./routes/issues"
import { SystemStatusPage } from "./routes/system-status"
import { AcceptInvitePage, ForgotPasswordPage, LoginPage, ResetPasswordPage } from "./routes/auth"
import { CustomerDetailPage, CustomersPage, NewCustomerPage } from "./routes/customers"
import { DiscountEditorPage, DiscountsPage } from "./routes/discounts"
import { TerabizzPage } from "./routes/terabizz"
import { FilesPage } from "./routes/files"
import { BlogPostEditorPage, BlogPostsPage } from "./routes/blog"
import { MenuEditorPage, MenusPage, PageEditorPage, PagesPage, PoliciesSettingsPage, PolicyEditorPage, RedirectsPage } from "./routes/content"
import { HomePage } from "./routes/home"
import { CustomDataSettingsPage } from "./routes/settings-custom-data"
import { ComingSoon, NotFound, SettingsComingSoon } from "./routes/misc"
import { GiftCardDetailPage, GiftCardsPage } from "./routes/gift-cards"
import { SegmentsPage } from "./routes/segments"
import { OrderPrintPage } from "./routes/order-print"
import { CheckoutSettingsPage } from "./routes/settings-checkout"
import { AccountSettingsPage } from "./routes/settings-accounts"
import { PaymentsSettingsPage } from "./routes/settings-payments"
import { PrivacySettingsPage, TrackingSettingsPage } from "./routes/settings-tracking"
import { CustomizePage } from "./routes/customize"
import { OnlineStorePage } from "./routes/online-store"
import { OrderDetailPage, OrdersPage } from "./routes/orders"
import { AbandonedPage } from "./routes/abandoned"
import { DraftEditorPage, DraftsPage } from "./routes/drafts"
import { CollectionEditorPage, CollectionsPage } from "./routes/collections"
import { InventoryPage } from "./routes/inventory"
import { ProductEditorPage } from "./routes/product-editor"
const OrderEditPage = lazyWithReload(() => import("./routes/order-edit").then((m) => ({ default: m.OrderEditPage })))
const BulkEditorPage = lazyWithReload(() => import("./routes/bulk-editor").then((m) => ({ default: m.BulkEditorPage })))
import { ProductsPage } from "./routes/products"
import { LotsPage } from "./routes/lots"
import { AccountPage } from "./routes/settings"
import { GeneralSettingsPage } from "./routes/settings-general"
import { BrandSettingsPage } from "./routes/settings-brand"
import { NotificationsSettingsPage, NotificationTemplatePage } from "./routes/settings-notifications"
import { StaffAlertsSettingsPage } from "./routes/settings-staff-alerts"
import { LocationsSettingsPage, ShippingSettingsPage, TaxCountryPage, TaxSettingsPage } from "./routes/settings-commerce"
import { MarketDetailPage, MarketsSettingsPage } from "./routes/settings-markets"
import { AppsSettingsPage } from "./routes/settings-apps"
import { AppGate } from "./apps/gate"
import { SettingsShell } from "./components/settings-shell"
import { Navigate } from "react-router-dom"
import { AddStaffPage, LegacyStaffRedirect, StaffMemberPage, UsersPage } from "./routes/users"
import type { Permission } from "./lib/types"

// Analytics is the heaviest area (charts, report editor): loaded only when opened.
const AnalyticsCostsPage = lazyWithReload(() => import("./routes/analytics-costs").then((m) => ({ default: m.AnalyticsCostsPage })))
const SettlementsPage = lazyWithReload(() => import("./routes/analytics-settlements").then((m) => ({ default: m.SettlementsPage })))
const InsightsPage = lazyWithReload(() => import("./analytics/Insights").then((m) => ({ default: m.InsightsPage })))
const AnalyticsDashboard = lazyWithReload(() => import("./analytics/Dashboard").then((m) => ({ default: m.AnalyticsDashboard })))
const LiveView = lazyWithReload(() => import("./analytics/LiveView").then((m) => ({ default: m.LiveView })))
const ReportsList = lazyWithReload(() => import("./analytics/ReportsList").then((m) => ({ default: m.ReportsList })))
const ReportView = lazyWithReload(() => import("./analytics/Report").then((m) => ({ default: m.ReportView })))
const SubscriptionsDashboard = lazyWithReload(() => import("./analytics/Subscriptions").then((m) => ({ default: m.SubscriptionsDashboard })))
// Terify apps (src/apps/<id>): downloaded only when opened and switched on.
const ReviewsApp = lazyWithReload(() => import("./apps/reviews/App").then((m) => ({ default: m.ReviewsApp })))
const SubscriptionsApp = lazyWithReload(() => import("./apps/subscriptions/App").then((m) => ({ default: m.SubscriptionsApp })))
const RecoveryApp = lazyWithReload(() => import("./apps/recovery/App").then((m) => ({ default: m.RecoveryApp })))
const UpsellsApp = lazyWithReload(() => import("./apps/upsells/App").then((m) => ({ default: m.UpsellsApp })))
// Admin → Messages (contact-form messages): its message page pulls in the subscription actions, so it loads when opened.
const InboxPage = lazyWithReload(() => import("./routes/inbox").then((m) => ({ default: m.InboxPage })))
const InboxMessagePage = lazyWithReload(() => import("./routes/inbox").then((m) => ({ default: m.InboxMessagePage })))
const lazyEl = (el: React.ReactNode) => <Suspense fallback={<div className="h-64 animate-pulse rounded-xl bg-white/60" />}>{el}</Suspense>

const guard = (perm: Permission, el: React.ReactNode) => <RequirePermission perm={perm}>{el}</RequirePermission>

const soon = (perm: Permission, title: string, body: string) => guard(perm, <ComingSoon title={title} body={body} />)

export const router = createBrowserRouter([
  // Public: no session needed
  { path: "/login", element: <LoginPage />, errorElement: <RouteError /> },
  { path: "/invite", element: <AcceptInvitePage /> },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  { path: "/reset-password", element: <ResetPasswordPage /> },
  {
    element: <RequireSession />,
    errorElement: <RouteError />,
    children: [
      { path: "orders/:id/print", element: guard("orders:read", <OrderPrintPage />) },
      {
        element: <AppShell />,
        errorElement: <RouteError />,
        children: [
          { index: true, element: <HomeRedirect><HomePage /></HomeRedirect> },
          { path: "orders", element: guard("orders:read", <OrdersPage />) },
          { path: "orders/drafts", element: guard("draft_orders:read", <DraftsPage />) },
          { path: "orders/drafts/new", element: guard("draft_orders:write", <DraftEditorPage />) },
          { path: "orders/drafts/:id", element: guard("draft_orders:read", <DraftEditorPage />) },
          { path: "orders/abandoned", element: guard("orders:read", <AbandonedPage />) },
          { path: "issues", element: guard("orders:read", <IssuesPage />) },
          { path: "status", element: guard("system_status:read", <SystemStatusPage />) },
          { path: "orders/:id", element: guard("orders:read", <OrderDetailPage />) },
          { path: "orders/:id/edit", element: guard("orders:write", lazyEl(<OrderEditPage />)) },
          { path: "products", element: guard("products:read", <ProductsPage />) },
          { path: "products/gift-cards", element: guard("gift_cards:read", <GiftCardsPage />) },
          { path: "products/lots", element: guard("products:read", <LotsPage />) },
          { path: "products/gift-cards/:id", element: guard("gift_cards:read", <GiftCardDetailPage />) },
          { path: "products/new", element: guard("products:write", <ProductEditorPage />) },
          { path: "products/bulk-edit", element: guard("products:write", lazyEl(<BulkEditorPage />)) },
          { path: "products/:id", element: guard("products:read", <ProductEditorPage />) },
          { path: "collections", element: guard("products:read", <CollectionsPage />) },
          { path: "collections/new", element: guard("products:write", <CollectionEditorPage />) },
          { path: "collections/:id", element: guard("products:read", <CollectionEditorPage />) },
          { path: "inventory", element: guard("inventory:read", <InventoryPage />) },
          { path: "customers", element: guard("customers:read", <CustomersPage />) },
          { path: "customers/new", element: guard("customers:write", <NewCustomerPage />) },
          { path: "customers/segments", element: guard("customers:read", <SegmentsPage />) },
          { path: "customers/:id", element: guard("customers:read", <CustomerDetailPage />) },
          { path: "messages", element: guard("customers:read", lazyEl(<InboxPage />)) },
          { path: "messages/:id", element: guard("customers:read", lazyEl(<InboxMessagePage />)) },
          { path: "discounts", element: guard("discounts:read", <DiscountsPage />) },
          { path: "discounts/new", element: guard("discounts:write", <DiscountEditorPage />) },
          { path: "discounts/:id", element: guard("discounts:read", <DiscountEditorPage />) },
          { path: "content", element: <Navigate to="/content/pages" replace /> },
          { path: "content/pages", element: guard("content:read", <PagesPage />) },
          { path: "content/pages/new", element: guard("content:write", <PageEditorPage />) },
          { path: "content/pages/:id", element: guard("content:read", <PageEditorPage />) },
          { path: "content/blog", element: guard("content:read", <BlogPostsPage />) },
          { path: "content/blog/new", element: guard("content:write", <BlogPostEditorPage />) },
          { path: "content/blog/:id", element: guard("content:read", <BlogPostEditorPage />) },
          { path: "content/files", element: guard("content:read", <FilesPage />) },
          { path: "content/menus", element: guard("content:read", <MenusPage />) },
          { path: "content/menus/:id", element: guard("content:read", <MenuEditorPage />) },
          { path: "content/redirects", element: guard("content:read", <RedirectsPage />) },
          { path: "content/*", element: soon("content:read", "Content", "This Content page isn't available yet. Use Pages, Menus, Files or Blog posts.") },
          { path: "analytics/reports", element: guard("analytics:read", lazyEl(<ReportsList />)) },
          { path: "analytics/reports/:key", element: guard("analytics:read", lazyEl(<ReportView />)) },
          { path: "analytics/live", element: guard("analytics:read", lazyEl(<LiveView />)) },
          { path: "analytics/subscriptions", element: guard("analytics:read", <AppGate id="subscriptions" name="Subscriptions">{lazyEl(<SubscriptionsDashboard />)}</AppGate>) },
          { path: "analytics/insights", element: guard("analytics:read", lazyEl(<InsightsPage />)) },
          { path: "analytics/costs", element: guard("analytics:read", lazyEl(<AnalyticsCostsPage />)) },
          { path: "analytics/settlements", element: guard("analytics:read", lazyEl(<SettlementsPage />)) },
          { path: "analytics/*", element: soon("analytics:read", "Analytics", "This page isn't available yet.") },
          { path: "analytics", element: guard("analytics:read", lazyEl(<AnalyticsDashboard />)) },
          { path: "apps/reviews/*", element: guard("reviews:read", <AppGate id="reviews" name="Reviews">{lazyEl(<ReviewsApp />)}</AppGate>) },
          { path: "apps/subscriptions/*", element: guard("subscriptions:read", <AppGate id="subscriptions" name="Subscriptions">{lazyEl(<SubscriptionsApp />)}</AppGate>) },
          { path: "apps/recovery/*", element: guard("subscriptions:read", <AppGate id="recovery" name="Recovery">{lazyEl(<RecoveryApp />)}</AppGate>) },
          { path: "apps/upsells/*", element: guard("upsells:read", <AppGate id="upsells" name="Cart & Checkout">{lazyEl(<UpsellsApp />)}</AppGate>) },
          { path: "terabizz", element: <TerabizzPage /> },
          { path: "online-store", element: guard("settings:read", <OnlineStorePage />) },
          { path: "online-store/customize", element: guard("content:read", <CustomizePage />) },
          { path: "*", element: <NotFound /> },
        ],
      },
      {
        path: "settings",
        element: <SettingsShell />,
        children: [
          { index: true, element: <Navigate to="/settings/general" replace /> },
          { path: "general", element: guard("settings:read", <GeneralSettingsPage />) },
          { path: "brand", element: guard("settings:read", <BrandSettingsPage />) },
          { path: "shipping", element: guard("settings:read", <ShippingSettingsPage />) },
          { path: "taxes", element: guard("settings:read", <TaxSettingsPage />) },
          { path: "taxes/:cc", element: guard("settings:read", <TaxCountryPage />) },
          { path: "markets", element: guard("settings:read", <MarketsSettingsPage />) },
          { path: "markets/new", element: guard("settings:write", <MarketDetailPage />) },
          { path: "markets/:id", element: guard("settings:read", <MarketDetailPage />) },
          { path: "checkout", element: guard("settings:read", <CheckoutSettingsPage />) },
          { path: "payments", element: guard("settings:read", <PaymentsSettingsPage />) },
          { path: "customer-events", element: guard("settings:read", <TrackingSettingsPage />) },
          { path: "customer-privacy", element: guard("settings:read", <PrivacySettingsPage />) },
          { path: "customer-accounts", element: guard("settings:read", <AccountSettingsPage />) },
          { path: "notifications", element: guard("settings:read", <NotificationsSettingsPage />) },
          // Personal: everyone manages their own alerts and devices.
          { path: "notifications/staff", element: <StaffAlertsSettingsPage /> },
          { path: "notifications/:key", element: guard("settings:read", <NotificationTemplatePage />) },
          { path: "locations", element: guard("settings:read", <LocationsSettingsPage />) },
          { path: "apps", element: guard("settings:read", <AppsSettingsPage />) },
          { path: "custom-data", element: guard("settings:read", <CustomDataSettingsPage />) },
          { path: "policies", element: guard("content:read", <PoliciesSettingsPage />) },
          { path: "policies/:id", element: guard("content:read", <PolicyEditorPage />) },
          { path: "account", element: <AccountPage /> },
          { path: "users", element: guard("staff:read", <UsersPage />) },
          { path: "users/new", element: guard("staff:write", <AddStaffPage />) },
          { path: "users/:id", element: guard("staff:read", <StaffMemberPage />) },
          { path: "staff", element: <LegacyStaffRedirect /> },
          { path: "activity", element: guard("logs:read", <ActivityPage />) },
          { path: "security-log", element: guard("logs:read", <SecurityLogPage />) },
          { path: "messages", element: guard("settings:read", <MessagesPage />) },
          { path: "store", element: <Navigate to="/settings/general" replace /> },
          { path: "*", element: <SettingsComingSoon /> },
        ],
      },
    ],
  },
])
